import { useEffect, useRef, useState } from "react";

import { getJobStatus } from "@/api/previews.api";
import type { JobStatusResponse } from "@/types/previews";
import { isJobTerminal } from "@/types/previews";

export const JOB_POLL_INTERVAL_MS = 3000;
export const JOB_POLL_MAX_CONSECUTIVE_ERRORS = 5;

export interface UseJobPollingResult {
  readonly status: JobStatusResponse | null;
  readonly error: Error | null;
  readonly stopped: boolean;
}

export function useJobPolling(jobId: string | null): UseJobPollingResult {
  const [status, setStatus] = useState<JobStatusResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [stopped, setStopped] = useState(false);
  const errorsRef = useRef(0);

  useEffect(() => {
    // Reset per-job state on every jobId change, not just on null.
    // A second non-null job would otherwise inherit the prior job's terminal
    // / error state and render as stopped before the first successful poll.
    setStatus(null);
    setError(null);
    setStopped(false);
    errorsRef.current = 0;

    if (jobId === null) {
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async (): Promise<void> => {
      try {
        const next = await getJobStatus(jobId);
        if (cancelled) return;
        errorsRef.current = 0;
        setStatus(next);
        setError(null);
        if (isJobTerminal(next)) {
          setStopped(true);
          return;
        }
      } catch (err) {
        if (cancelled) return;
        errorsRef.current += 1;
        setError(err as Error);
        if (errorsRef.current >= JOB_POLL_MAX_CONSECUTIVE_ERRORS) {
          setStopped(true);
          return;
        }
      }
      // Guard: if the effect was cancelled during the await, do NOT schedule
      // another tick — the cleanup has already fired and a zombie timer
      // would fire a getJobStatus call for the superseded jobId.
      if (cancelled) return;
      timer = setTimeout(tick, JOB_POLL_INTERVAL_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [jobId]);

  return { status, error, stopped };
}
